import { Clock, Instagram, Mail, MapPin, Navigation, Phone } from "lucide-react";
import Reveal from "./Reveal";
import { WhatsAppIcon } from "./icons";
import ContactForm from "./ContactForm";
import { SITE, WA } from "../data/site";

const DETAILS = [
    {
        icon: MapPin,
        label: "Visit",
        value: SITE.address,
        href: SITE.mapsUrl,
        testid: "contact-detail-visit",
    },
    {
        icon: Clock,
        label: "Hours",
        value: SITE.hours,
        testid: "contact-detail-hours",
    },
    {
        icon: Phone,
        label: "Call",
        value: SITE.phoneDisplay,
        href: SITE.phoneHref,
        testid: "contact-detail-call",
    },
    {
        icon: Mail,
        label: "Email",
        value: SITE.email,
        href: `mailto:${SITE.email}`,
        testid: "contact-detail-email",
    },
    {
        icon: Instagram,
        label: "Instagram",
        value: SITE.instagramHandle,
        href: SITE.instagram,
        testid: "contact-detail-instagram",
    },
];

const Contact = () => (
    <section id="contact" data-testid="contact-section" className="scroll-mt-24 py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
            <Reveal>
                <div className="max-w-2xl">
                    <p className="eyebrow">Location &amp; Contact</p>
                    <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight text-saga-red sm:text-5xl">
                        Find us in Himayatnagar
                    </h2>
                </div>
            </Reveal>

            <div className="mt-16 grid grid-cols-1 gap-12 lg:grid-cols-12">
                <div className="space-y-10 lg:col-span-6">
                    <Reveal>
                        <div className="overflow-hidden rounded-2xl border border-saga-red/20 shadow-[0_16px_48px_rgba(105,0,3,0.08)]">
                            <iframe
                                title="Paan Saga location map — Himayatnagar Main Road, Hyderabad"
                                src={SITE.mapEmbed}
                                data-testid="location-map"
                                className="h-80 w-full sm:h-96"
                                loading="lazy"
                                allowFullScreen
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        </div>
                        <a
                            href={SITE.mapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="contact-directions-btn"
                            className="mt-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-saga-red underline-offset-4 hover:underline"
                        >
                            <Navigation className="h-4 w-4" aria-hidden="true" />
                            Open in Google Maps
                        </a>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <ul className="divide-y divide-saga-red/10 border-y hairline">
                            {DETAILS.map((d) => (
                                <li key={d.label} className="flex items-center gap-4 py-4">
                                    <d.icon className="h-5 w-5 shrink-0 text-saga-red" aria-hidden="true" />
                                    <span className="w-20 shrink-0 text-xs font-semibold uppercase tracking-[0.2em] text-saga-ink/50">
                                        {d.label}
                                    </span>
                                    {d.href ? (
                                        <a
                                            href={d.href}
                                            target={d.href.startsWith("http") ? "_blank" : undefined}
                                            rel={d.href.startsWith("http") ? "noopener noreferrer" : undefined}
                                            data-testid={d.testid}
                                            className="text-sm font-medium text-saga-ink underline-offset-4 transition-colors hover:text-saga-red hover:underline sm:text-base"
                                        >
                                            {d.value}
                                        </a>
                                    ) : (
                                        <span data-testid={d.testid} className="text-sm font-medium text-saga-ink sm:text-base">
                                            {d.value}
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                        <a
                            href={WA.contact}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="contact-whatsapp-btn"
                            className="mt-6 inline-flex items-center gap-2 rounded-full border border-saga-red px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] text-saga-red transition-colors hover:bg-saga-red hover:text-saga-cream"
                        >
                            <WhatsAppIcon className="h-4 w-4" />
                            WhatsApp Us
                        </a>
                    </Reveal>
                </div>

                <div className="lg:col-span-6">
                    <Reveal delay={0.15}>
                        <div className="rounded-2xl border border-saga-red/15 bg-saga-creamdark/40 p-8 sm:p-10">
                            <h3 className="font-display text-3xl font-semibold text-saga-red">Send an enquiry</h3>
                            <p className="mt-2 text-sm leading-relaxed text-saga-ink/60">
                                Your message reaches us by email, and WhatsApp opens too — so we can reply as quickly as possible.
                            </p>
                            <div className="mt-8">
                                <ContactForm />
                            </div>
                        </div>
                    </Reveal>
                </div>
            </div>
        </div>
    </section>
);

export default Contact;
